import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

interface FetchWorkerTypesParams {
  workplaceId: string;
  showHidden: boolean;
}

const workerTypeSchema = z.object({
  name: z.string(),
  visible: z.boolean(),
  contracted: z.boolean().optional(),
});

export const workerTypesResponseSchema = z.object({
  data: z.object({
    workerTypes: z.array(workerTypeSchema),
    workplaceId: z.string().optional(),
  }),
});

export const calendarSettingFormSchema = z.object({
  workerTypes: z
    .array(workerTypeSchema)
    .refine((workerTypes) => workerTypes.some((workerType) => workerType.visible), {
      message: "At least one workerType must be selected",
    }),
});
export type WorkerTypesResponse = z.infer<typeof workerTypesResponseSchema>;
export type WorkerTypes = WorkerTypesResponse["data"];

export function useFetchWorkplaceWorkerTypes(
  params: FetchWorkerTypesParams,
  options?: UseGetQueryOptions<WorkerTypesResponse>
): UseQueryResult<WorkerTypesResponse> {
  const { workplaceId, showHidden } = params;
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/worker-types`,
    queryParams: {
      showHidden,
    },
    responseSchema: workerTypesResponseSchema,
    refetchOnWindowFocus: false,
    ...options,
    enabled: options?.enabled && isDefined(workplaceId),
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_TYPES_FAILURE,
    },
  });
}
